import { Card, Text } from "@overmap-ai/blocks"
import { type IssueUpdate, IssueUpdateChange, type Stored } from "@overmap-ai/common"
import { selectIssueTypeStatusMapping, selectUserById, selectUsersMapping } from "@overmap-ai/core"
import { memo } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector } from "../../store"
import { formatCompactDate } from "../../utils/date"
import { getUserDisplayLabel } from "../../utils/users"
import { IssuePriorityIcon } from "../Icons/IssuePriorityIcon"
import { IssueTypeStatusIcon } from "../Icons/IssueTypeStatusIcon"
import { TimelineIconRail } from "../TimelineIconRail/TimelineIconRail"
import type { IssueTimelineSize } from "./typings"

interface IssueTimelineIssueUpdateItemProps {
	issueUpdate: Stored<IssueUpdate>
	size: IssueTimelineSize
}

export const IssueTimelineIssueUpdateItem = memo((props: IssueTimelineIssueUpdateItemProps) => {
	const { issueUpdate, size } = props

	const usersMapping = useAppSelector(selectUsersMapping)
	const issueTypeStatusMapping = useAppSelector(selectIssueTypeStatusMapping)
	const user = useAppSelector(selectUserById(issueUpdate.created_by ?? -1))
	const formattedDate = formatCompactDate(issueUpdate.submitted_at)

	const changes = issueUpdate.changes

	return (
		<>
			{(Object.keys(changes) as IssueUpdateChange[]).map((updateType) => {
				const icon =
					updateType === IssueUpdateChange.DUE_DATE
						? modelIconMapping.dueDate
						: updateType === IssueUpdateChange.ASSIGNED_TO
							? modelIconMapping.assignee
							: updateType === IssueUpdateChange.PRIORITY
								? modelIconMapping.priority
								: updateType === IssueUpdateChange.STATUS
									? modelIconMapping.status
									: modelIconMapping.edit

				switch (updateType) {
					case IssueUpdateChange.DESCRIPTION:
						return (
							<div key={updateType} className="flex h-max w-full gap-4">
								<TimelineIconRail icon={icon} size={size} />
								<Card className="flex min-w-0 grow flex-col gap-3" size={size}>
									<Text size={size} accentColor="base" className="line-clamp-2">
										<Text highContrast>{getUserDisplayLabel(user)}</Text>
										&nbsp;updated the description&nbsp;&#8226;&nbsp;
										{formattedDate}
									</Text>
								</Card>
							</div>
						)
					case IssueUpdateChange.TITLE: {
						const value = changes[updateType]
						return (
							<div key={updateType} className="flex h-max w-full gap-4">
								<TimelineIconRail icon={icon} size={size} />
								<Card className="flex min-w-0 grow flex-col gap-3" size={size}>
									<Text size={size} accentColor="base" className="line-clamp-2">
										<Text highContrast>{getUserDisplayLabel(user)}</Text>
										&nbsp;updated the title to&nbsp;
										<Text highContrast>{value}</Text>
										&nbsp;&#8226;&nbsp;
										{formattedDate}
									</Text>
								</Card>
							</div>
						)
					}
					case IssueUpdateChange.DUE_DATE: {
						const value = changes[updateType]
						return (
							<div key={updateType} className="flex h-max w-full gap-4">
								<TimelineIconRail icon={icon} size={size} />
								<Card className="flex min-w-0 grow flex-col gap-3" size={size}>
									<Text size={size} accentColor="base" className="line-clamp-2">
										<Text highContrast>{getUserDisplayLabel(user)}</Text>
										&nbsp;updated the due date to&nbsp;
										<Text highContrast>{value ? formatCompactDate(value as string) : "none"}</Text>
										&nbsp;&#8226;&nbsp;
										{formattedDate}
									</Text>
								</Card>
							</div>
						)
					}
					case IssueUpdateChange.ASSIGNED_TO: {
						const value = changes[updateType]
						const assignee = value ? usersMapping[value.id] : null

						return (
							<div key={updateType} className="flex h-max w-full gap-4">
								<TimelineIconRail icon={icon} size={size} />
								<Card className="flex min-w-0 grow flex-col gap-3" size={size}>
									<Text size={size} accentColor="base" className="line-clamp-2">
										<Text highContrast>{getUserDisplayLabel(user)}</Text>
										&nbsp;updated the assignee to&nbsp;
										{assignee ? (
											<Text highContrast>{getUserDisplayLabel(assignee)}</Text>
										) : (
											<Text highContrast>none</Text>
										)}
										&nbsp;&#8226;&nbsp;
										{formattedDate}
									</Text>
								</Card>
							</div>
						)
					}
					case IssueUpdateChange.PRIORITY: {
						const value = changes[updateType]!

						return (
							<div key={updateType} className="flex h-max w-full gap-4">
								<TimelineIconRail icon={icon} size={size} />
								<Card className="flex min-w-0 grow flex-col gap-3" size={size}>
									<Text size={size} accentColor="base" className="line-clamp-2">
										<Text highContrast>{getUserDisplayLabel(user)}</Text>
										&nbsp;updated the priority to&nbsp;
										<IssuePriorityIcon priority={value.uuid} className="align-middle" />
										&nbsp;
										<Text highContrast>{value.name}</Text>
										&nbsp;&#8226;&nbsp;
										{formattedDate}
									</Text>
								</Card>
							</div>
						)
					}
					case IssueUpdateChange.STATUS: {
						const value = changes[updateType]!
						const issueTypeStatus = issueTypeStatusMapping[value.uuid]

						return (
							<div key={updateType} className="flex h-max w-full gap-4">
								<TimelineIconRail icon={icon} size={size} />
								<Card className="flex min-w-0 grow flex-col gap-3" size={size}>
									<Text size={size} accentColor="base" className="line-clamp-2">
										<Text highContrast>{getUserDisplayLabel(user)}</Text>
										&nbsp;updated the status to&nbsp;
										{issueTypeStatus ? (
											<>
												<IssueTypeStatusIcon
													issueTypeStatusId={issueTypeStatus.uuid}
													className="align-middle"
												/>
												&nbsp;
												<Text highContrast>{issueTypeStatus.name}</Text>
											</>
										) : (
											<Text highContrast>(deleted)</Text>
										)}
										&nbsp;&#8226;&nbsp;
										{formattedDate}
									</Text>
								</Card>
							</div>
						)
					}
				}
			})}
		</>
	)
})
IssueTimelineIssueUpdateItem.displayName = "IssueTimelineIssueUpdateItem"
